import { countryCodes, type CountryData, type OptionGroup } from "types";

class CountryService {
  private static instance: CountryService;
  private countryMap: Map<string, CountryData>;
  private phoneCodeMap: Map<string, CountryData>;
  private isoCodeMap: Map<string, CountryData>;

  private constructor() {
    this.countryMap = new Map();
    this.phoneCodeMap = new Map();
    this.isoCodeMap = new Map();
    this.initializeMaps();
  }

  public static getInstance(): CountryService {
    if (!CountryService.instance) {
      CountryService.instance = new CountryService();
    }
    return CountryService.instance;
  }

  private initializeMaps(): void {
    countryCodes.forEach((country) => {
      // Country name mapping
      const countries = country.country.split(" / ");
      countries.forEach((countryName) => {
        this.countryMap.set(countryName.toLowerCase().trim(), country);
      });

      // Phone code mapping
      this.phoneCodeMap.set(country.value, country);

      // ISO code mapping
      country.isoCodes.forEach((isoCode) => {
        this.isoCodeMap.set(isoCode.toLowerCase(), country);
      });
    });
  }

  public getByName(countryName: string): CountryData | undefined {
    return this.countryMap.get(countryName.toLowerCase());
  }

  public getByPhoneCode(phoneCode: string): CountryData | undefined {
    return this.phoneCodeMap.get(phoneCode);
  }

  public getByIsoCode(isoCode: string): CountryData | undefined {
    return this.isoCodeMap.get(isoCode.toLowerCase());
  }

  public getIsoCode(countryName: string): string {
    const countryData = this.getByName(countryName);
    if (!countryData) return "";

    // For combined countries, try to match specific country
    if (countryData.isoCodes.length > 1) {
      const lowerCountryName = countryName.toLowerCase();
      if (
        lowerCountryName.includes("united states") ||
        lowerCountryName.includes("usa")
      ) {
        return "US";
      }
      if (lowerCountryName.includes("canada")) {
        return "CA";
      }
      if (lowerCountryName.includes("russia")) {
        return "RU";
      }
      if (lowerCountryName.includes("kazakhstan")) {
        return "KZ";
      }
    }

    // Return first ISO code as default
    return countryData.isoCodes[0];
  }

  public getCodeByName(countryName: string): string | undefined {
    const exactMatch = this.getByName(countryName);
    if (exactMatch) return exactMatch.value;

    const partialMatch = Array.from(this.countryMap.values()).find(
      (cc) =>
        cc.country.toLowerCase().includes(countryName.toLowerCase()) ||
        countryName.toLowerCase().includes(cc.country.toLowerCase())
    );
    return partialMatch?.value;
  }

  public getGroupedCodes(): OptionGroup[] {
    const regions = [...new Set(countryCodes.map((c) => c.region))];
    return regions.map((region) => ({
      label: region,
      options: countryCodes.filter((country) => country.region === region),
    }));
  }
}

export default CountryService;
